import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Container,
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  InputAdornment,
  Stack,
} from "@mui/material";
import {
  LockReset as LockResetIcon,
  Email,
  ArrowBack,
  CheckCircle,
} from "@mui/icons-material";
import * as myApi from "../../components/Api";
import { validateEmail } from "../../utils/formValidation";
import AuthBackgroundSlideshow from "../../components/AuthBackground/Slideshow";

/**
 * Forgot Password Page with Material UI
 * Features:
 * - Clean, centered card layout
 * - Email validation
 * - Success message after sending
 * - Back to sign in link
 */
export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);

    // Validate email
    const emailValidation = validateEmail(email);
    if (!emailValidation.valid) {
      setEmailError(emailValidation.message);
      toast(emailValidation.message, { type: "error" });
      setLoading(false);
      return;
    }
    setEmailError("");

    const data = {
      data_email: email,
      data_kode: "passwd",
    };

    try {
      const response = await myApi.post("/forgotpwd", data);

      if (response.data.status === "Email Exist") {
        setSuccess(true);
        toast.success("Password reset link sent to your email!", {
          position: "top-right",
          autoClose: 5000,
        });
      } else if (response.data.status === "Email Not Found") {
        toast.error("Email address not found in our system", {
          position: "top-right",
          autoClose: 5000,
        });
      } else {
        toast.error(response.data.message || "Failed to send reset link", {
          position: "top-right",
          autoClose: 5000,
        });
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || "Failed to send reset link";
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    if (emailError) {
      const validation = validateEmail(e.target.value);
      setEmailError(validation.valid ? "" : validation.message);
    }
  };

  return (
    <>
      {/* Animated Background Slideshow */}
      <AuthBackgroundSlideshow />
      <Container maxWidth="sm" sx={{ minHeight: "100vh", py: 4 }}>
      <Paper
        elevation={0}
        sx={{
          p: 4,
          borderRadius: 3,
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        }}
      >
        {/* Icon */}
        <Box sx={{ mb: 3, textAlign: "center" }}>
          <LockResetIcon sx={{ fontSize: 64, color: "primary.main", mb: 2 }} />
        </Box>

        {/* Header */}
        <Typography variant="h4" component="h1" gutterBottom fontWeight={600}>
          Forgot Password?
        </Typography>
        <Typography variant="body1" color="text.secondary" mb={4}>
          No worries! Enter your email and we'll send you reset instructions.
        </Typography>

        {/* Success Message */}
        {success && (
          <Alert severity="success" icon={<CheckCircle />} sx={{ mb: 3 }}>
            Password reset link sent to <strong>{email}</strong>. Please check
            your email.
          </Alert>
        )}

        {/* Form */}
        {!success && (
          <form onSubmit={handleSubmit}>
            <Stack spacing={3}>
              <TextField
                fullWidth
                required
                label="Email Address"
                value={email}
                onChange={handleEmailChange}
                error={!!emailError}
                helperText={emailError}
                placeholder="email@example.com"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email color="action" />
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                type="submit"
                variant="contained"
                size="large"
                fullWidth
                disabled={loading}
                startIcon={<LockResetIcon />}
                sx={{
                  py: 1.5,
                  fontSize: "1rem",
                  fontWeight: 600,
                }}
              >
                {loading ? "Sending..." : "Send Reset Link"}
              </Button>

              <Button
                component={Link}
                to="/auth/sign-in/"
                variant="text"
                fullWidth
                startIcon={<ArrowBack />}
                sx={{
                  textTransform: "none",
                  fontWeight: 500,
                }}
              >
                Back to Sign In
              </Button>
            </Stack>
          </form>
        )}

        {/* Footer */}
        <Box
          sx={{
            mt: 4,
            pt: 3,
            borderTop: 1,
            borderColor: "divider",
            textAlign: "center",
          }}
        >
          <Typography variant="caption" color="text.secondary">
            © 2026 KARSA NUSA. All rights reserved.
          </Typography>
        </Box>
      </Paper>
    </Container>
    </>
  );
}
